import React, { useState, useEffect } from 'react';
import { Navigate, useParams } from "react-router-dom";

import { Button } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { 
  ModelBase,
  PlayerObject,
} from "@models";
import { SourceBook } from "@dndModels";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import LoadingBox from "@components/input/LoadingBox";
import ButtonBox from "@components/input/buttons/ButtonBox";
import StringBox from "@components/input/strings/StringBox";

import ModelBaseInput from "@dndComponents/ModelBaseInput";
import CheckBox from "@components/input/CheckBox";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

export default function SourceBookEdit() {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [obj, setObj] = useState<SourceBook>(new SourceBook());
  const [third_party, setThirdParty] = useState(false);
  const [change_time, setChanged] = useState(-1);

  useEffect(() => {
    if(!fetched) {
      load();
    }
  });

  const load = () => {
    setFetched(true);
    if (id && id !== "") {
      setLoading(true);
      const onComplete = (res: PlayerObject[] | ModelBase[]) => {
        if (res.length === 1 && res[0] instanceof SourceBook) {
          setObj(res[0]);
          setThirdParty(res[0].third_party);
        }
        setLoading(false);
      };
      const onProgress = () => {};
      api.getObjectsWithCallbacks({ 
        data_type: "source_book", filter: { _id: id },
        onComplete, onProgress 
      });
    } else {
      const new_obj = obj;
      if (api.user && !api.user.admin && obj.name === "" && obj.owner_id === "") {
        new_obj.owner_id = api.user._id;
        new_obj.source_book_type = "Homebrew";
        new_obj.is_free = false;
        setObj(new_obj);
      }
    }
  }

  const submit = () => {
    if (api.user && 
      (api.user.admin || 
        obj._id === "" || 
        obj.owner_id === api.user._id)) {
      setProcessing(true);
      if (!api.user.admin || obj.source_books_old.filter(o => o.id === "Personal").length > 0) {
        obj.owner_id = api.user._id;
      } else if (api.user.admin && obj.source_books_old.filter(o => o.type === "Homebrew").length === 0) {
        obj.owner_id = "";
      }
      api.upsertObject("source_book", obj).then(res => {
        setProcessing(false);
        setNavigateTo("/dandd/source_book");
      });
    }
  }

  const renderPurchase = () => {
    // debugger;
    if (obj.is_free) {
      return null;
    } else {
      const user = api.user;
      if (user) {
        if (user.purchased_sb_ids.includes(obj._id)) {
          // You have purchased this source book
          return (
            <Grid container spacing={0} direction="row">
              <Grid
                size={{
                  xs: 6
                }}>
                You have purchased this source book
              </Grid>
              <Grid
                size={{
                  xs: 6
                }}>
                <ButtonBox 
                  name="Unpurchase this Source Book"
                  disabled={processing || !api.online}
                  onClick={() => {
                    const user = api.user;
                    if (user) {
                      setProcessing(true);
                      user.purchased_sb_ids = user.purchased_sb_ids.filter(o => o !== obj._id);
                      api.updateObject("user", user).then(res => {
                        setProcessing(false);
                      });
                    }
                  }}
                />
              </Grid>
            </Grid>
          );
        } else if (user.shared_sb_ids.includes(obj._id)) {
          // This source book has been shared with you, but you can still purchase it
          return [
            <Grid key="message">
              This source book has been shared with you, but you can still purchase it
            </Grid>,
            renderPurchaseButton()
          ];
        } else {
          // Purchase
          return renderPurchaseButton();
        }
      } else {
        // Login to Purchase
        return (
          <Grid>You need to log in to be able to purchase a source book.
                      </Grid>
        );
      }
    }
  }

  const renderPurchaseButton = () => {
    return (
      <Grid key="purchase">
        <ButtonBox 
          name="Purchase this Source Book"
          disabled={processing || !api.online}
          onClick={() => {
            const user = api.user;
            if (user) {
              setProcessing(true);
              user.purchased_sb_ids.push(obj._id);
              api.updateObject("user", user).then(res => {
                setProcessing(false);
              });
            }
          }}
        />(Currently just sets it to purchased in the db.  There are no charges.)
              </Grid>
    );
  }

  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);
  if (!fetched || loading) {
    const height = window.innerHeight - 64;
    return (<LoadingBox size={height * 0.5} height={height} width={window.innerWidth} />);
  } else if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else { 
    return (
      <ModelBaseInput 
        obj={obj}
        disabled={disabled}
        change_time={change_time}
        onChange={(changed: ModelBase) => {
          if (changed instanceof SourceBook) {
            const new_obj = obj;
            new_obj.copy(changed);
            setObj(new_obj);
          }
        }}
        submitControls={
          <>
            { disabled ?
              <Grid
                className="padded"
                size={{
                  xs: 12
                }}>
                { renderPurchase() }
              </Grid>
            :
              <>
                <Grid
                  className="padded"
                  size={{
                    xs: 12
                  }}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={processing}
                    onClick={submit}>
                    Submit
                  </Button>
                  <Button
                    variant="contained"
                    disabled={processing}
                    style={{ marginLeft: "4px" }}
                    onClick={ () => { 
                      setNavigateTo(`/dandd/source_book`);
                    }}>
                    Cancel
                  </Button>
                </Grid>
              </>
            }
          </>
        }>
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 12
            }}>
            { disabled ? <><span className="header">Abbreviation:</span> {obj.abbreviation}</> : 
              <StringBox 
                name="Abbreviation"
                value={obj.abbreviation}
                onBlur={(changed: string) => {
                  const new_obj = obj;
                  new_obj.abbreviation = changed;
                  setObj(new_obj);
                  setChanged(common_util.get_time_number());
                }}
              />
            }
          </Grid>
          { disabled ?
            <>
            { third_party && <Grid
              className="header"
              size={{
                xs: 12
              }}>Third Party</Grid> }
            </>
          :
            <Grid
              size={{
                xs: 12
              }}>
              <CheckBox 
                name="Third Party"
                value={third_party}
                onChange={(changed: boolean) => {
                  const new_obj = obj;
                  new_obj.third_party = changed;
                  setObj(new_obj);
                  setThirdParty(changed);
                  setChanged(common_util.get_time_number());
                }}
              />
            </Grid>
          }
        </Grid>
      </ModelBaseInput>
    ); 
  }
}